/**
 * Système de toasts unifié pour ORION CRM.
 * =======================================================================
 *
 * Pourquoi : on avait ~169 usages incohérents (alert, confirm, setToast
 * locaux par composant). Cette API unique remplace tout ça.
 *
 * Usage depuis un composant React :
 *   import { toast } from '../services/toast';
 *   toast.success('Prospect créé');
 *   toast.error('Impossible d\'enregistrer');
 *   toast.warning('Quota presque atteint');
 *   toast.info('Synchronisation en cours…');
 *
 * Usage depuis du code non-React (services, helpers) :
 *   import { toast } from '../services/toast';
 *   toast.error('Connexion API perdue');
 *
 * Le composant <ToastContainer /> doit être monté une seule fois au
 * niveau racine (App.tsx) pour afficher la pile.
 */

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastOptions {
    /** Durée d'affichage en ms ; 0 = persistant. Défaut : 4000 (success/info) ou 6000 (error/warning). */
    duration?: number;
    /** Bouton d'action optionnel (ex: « Annuler », « Voir »). */
    actionLabel?: string;
    onAction?: () => void;
}

export interface ToastEntry extends ToastOptions {
    id: string;
    type: ToastType;
    message: string;
    createdAt: number;
}

const MAX_STACK = 5;
const listeners = new Set<(toasts: ToastEntry[]) => void>();
let stack: ToastEntry[] = [];

function emit(): void {
    for (const l of listeners) l([...stack]);
}

function defaultDuration(type: ToastType): number {
    return type === 'error' || type === 'warning' ? 6000 : 4000;
}

function push(type: ToastType, message: string, opts?: ToastOptions): string {
    const id = `t_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const entry: ToastEntry = {
        id,
        type,
        message,
        createdAt: Date.now(),
        ...opts,
    };
    // Cap la pile : si déjà MAX_STACK, on retire le plus ancien.
    stack = [...stack.slice(-(MAX_STACK - 1)), entry];
    emit();
    const duration = opts?.duration ?? defaultDuration(type);
    if (duration > 0) {
        setTimeout(() => dismiss(id), duration);
    }
    return id;
}

function dismiss(id: string): void {
    const next = stack.filter(t => t.id !== id);
    if (next.length !== stack.length) {
        stack = next;
        emit();
    }
}

function clear(): void {
    if (stack.length === 0) return;
    stack = [];
    emit();
}

export const toast = {
    success: (message: string, opts?: ToastOptions) => push('success', message, opts),
    error:   (message: string, opts?: ToastOptions) => push('error', message, opts),
    warning: (message: string, opts?: ToastOptions) => push('warning', message, opts),
    info:    (message: string, opts?: ToastOptions) => push('info', message, opts),
    dismiss,
    clear,
};

/**
 * S'abonne aux changements de la pile. Renvoie une fonction pour se
 * désabonner. Utilisé par ToastContainer.
 */
export function subscribeToasts(listener: (toasts: ToastEntry[]) => void): () => void {
    listeners.add(listener);
    listener([...stack]); // émission initiale
    return () => { listeners.delete(listener); };
}
